import TiktokIcon from '@/components/icons/tiktok-icon';
import { ElementProps } from '@/components/sections/builder/types';

import {
  FooterElementDataType,
  FooterSectionType,
} from '@/components/sections/builder/elements/footer/footer/types';
import { Checkbox } from '@/components/ui/checkbox';
import Image from 'next/image';

import { useBuilderStore } from '@/stores/builder';
import {
  BetweenHorizontalStart,
  FacebookIcon,
  InstagramIcon,
  TwitterIcon,
  YoutubeIcon,
} from 'lucide-react';
import Link from 'next/link';
import React from 'react';
import { shallow } from 'zustand/shallow';

const defaultData: FooterSectionType = {
  name: 'Brand Info',
  heading: 'Brand Info',
  subtext: 'Info',
  isShowSocialMedia: true,
  logoUrl: '',
  logoWidth: 170,
};

export const ElementContent = ({ element }: ElementProps) => {
  const data = (element.data || defaultData) as FooterSectionType;
  const parentData = (element.parentData || {}) as FooterElementDataType;

  const SOCIAL_MEDIA = [
    { key: 'twitter', label: 'Twitter', icon: <TwitterIcon /> },
    { key: 'facebook', label: 'Facebook', icon: <FacebookIcon /> },
    { key: 'instagram', label: 'Instagram', icon: <InstagramIcon /> },
    { key: 'youtube', label: 'Youtube', icon: <YoutubeIcon /> },
    {
      key: 'tiktok',
      label: 'Tiktok',
      icon: <TiktokIcon color={parentData.colorSchemeFooter?.text || '#52535B'} />,
    },
  ];
  return (
    <>
      <div className='w-full '>
        <div
          className={`flex ${
            parentData.alignment === 'left'
              ? 'justify-start'
              : parentData.alignment === 'right'
              ? 'justify-end'
              : 'justify-center'
          }`}
        >
          {data?.logoUrl && (
            <Link href='/' className='mb-3'>
              <Image
                src={data?.logoUrl as string}
                alt='Logo'
                className='header-logo'
                style={{ width: `${data?.logoWidth}px` }}
                width={100}
                height={56}
                quality={100}
                unoptimized
              />
            </Link>
          )}
        </div>

        <h2
          className='content-footer-text mb-1 text-lg font-medium'
          dangerouslySetInnerHTML={{ __html: data.heading || '' }}
        ></h2>
        <p
          className='content-footer-text mt-3'
          dangerouslySetInnerHTML={{ __html: data.subtext || '' }}
        ></p>
        {data.isShowSocialMedia && (
          <div className='flex items-center gap-x-3'>
            {SOCIAL_MEDIA.map((social) => {
              const keyUrl = `${social.key}Url` as keyof FooterElementDataType;
              const keyIconUrl = `${social.key}IconUrl` as keyof FooterElementDataType;

              if (!parentData[keyUrl]) {
                return null;
              }

              return (
                <Link
                  key={social.key}
                  href={parentData[keyUrl] as string}
                  className='mb-2 '
                  style={{ justifyContent: parentData.alignment || 'left' }}
                >
                  <div className='flex h-6 w-6'>
                    {parentData[keyIconUrl] ? (
                      <Image
                        src={parentData[keyIconUrl] as string}
                        width={24}
                        height={24}
                        layout='responsive'
                        alt={`icon-${social.key}`}
                        className='object-cover'
                      />
                    ) : (
                      social.icon
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
      <style jsx>
        {`
          .content-footer-text {
            text-align: ${parentData.alignment};
          }

          .header-logo {
            display: inline-block;
            text-align: ${parentData.alignment};
          }
        `}
      </style>
    </>
  );
};

export const ElementPreviewIcon = BetweenHorizontalStart;

export function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setChildData: state.setChildData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = element.data as FooterSectionType;

  return (
    <div>
      <div className='mb-1 text-base font-bold md:mt-4'>Social media icons</div>
      <div className='my-3 flex gap-x-1'>
        <Checkbox
          id='separator'
          className='mx-2'
          checked={data.isShowSocialMedia}
          onCheckedChange={(checked) => {
            builder.setChildData(element.parentId as string, element.id, {
              isShowSocialMedia: !!checked,
            });
          }}
        />
        <label htmlFor='separator'>Show Social Media Icons</label>
      </div>
    </div>
  );
}

const FooterSectionBrandInfo = {
  type: 'footer_section_brand_info',
  group: 'footer_section',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: ElementPreviewIcon,
  Icon: ElementPreviewIcon,
  childs: [],
};

export default FooterSectionBrandInfo;
